import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import EMPButton from "../../shared/components/EMPButton/EMPButton";
import EMPSelectField from "../../shared/components/EMPSelectField/EMPSelectField";
import styles from "./PreOrderReviewModal.module.scss";
import {
  CANCELLATION_REASONS,
  OTHER_REASON_VALUE,
  buildDealerRejectedReason,
  getCancellationReason,
} from "./cancellationReasons";

const buttonSx = {
  width: "auto !important",
  minWidth: "unset !important",
  height: "auto",
  padding: "10px 20px",
  whiteSpace: "nowrap",
};

const CancellationReasonModal = ({
  open,
  orderId,
  onClose,
  onGoBack,
  onCancelOrder,
  onCancelSuccess,
}) => {
  const { t } = useTranslation();

  const [selectedReason, setSelectedReason] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [isCanceled, setIsCanceled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!open) {
      setSelectedReason("");
      setOtherReason("");
      setIsCanceled(false);
      setIsSubmitting(false);
      setErrorMessage("");
    }
  }, [open]);

  const isOtherReason = selectedReason === OTHER_REASON_VALUE;

  const isCancelDisabled =
    isSubmitting || !selectedReason || (isOtherReason && !otherReason.trim());

  const cancellationReasonMenuItems = [
    {
      id: "cancellation-reason-placeholder",
      text: "Select one",
      value: "",
      disabled: false,
    },
    ...CANCELLATION_REASONS.map((reason) => ({
      id: `cancellation-reason-${reason.value}`,
      text: reason.label,
      value: reason.value,
      disabled: false,
    })),
  ];

  const handleReasonChange = (event) => {
    const value = event.target.value;

    setSelectedReason(value);

    if (value !== OTHER_REASON_VALUE) {
      setOtherReason("");
    }
  };

  const handleOtherReasonChange = (event) => {
    setOtherReason(event.target.value);
  };

  const getErrorMessage = (error) => {
    const status = error?.response?.status;
    if (status === 404) {
      return t(
        "CustomerOrderDetails.preOrder.cancelModal.error.preOrderNotFound",
        {
          defaultValue: "Preorder cannot be found. Please refresh the page.",
        }
      );
    }
    if (status === 422) {
      return t(
        "CustomerOrderDetails.preOrder.cancelModal.error.preOrderCannotBeCanceled",
        {
          defaultValue:
            "Preorder cannot be canceled. Please refresh the page to get the most up to date order status.",
        }
      );
    }
    return t("CustomerOrderDetails.preOrder.cancelModal.error.tryAgain", {
      defaultValue: "Try Again",
    });
  };

  const handleCancelOrder = async () => {
    const dealerRejectedReason = buildDealerRejectedReason(
      selectedReason,
      otherReason
    );

    const selectedReasonDetails = getCancellationReason(selectedReason);

    const cancellationDetails = {
      orderId,
      reasonLabel: selectedReasonDetails?.label || "",
      reasonCode: dealerRejectedReason,
      reasonText: isOtherReason ? otherReason.trim() : "",
    };

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      await onCancelOrder(cancellationDetails);
      setIsCanceled(true);
      onCancelSuccess(cancellationDetails);
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseConfirmation = () => {
    setIsCanceled(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === "backdropClick" || reason === undefined) {
          onClose();
        }
      }}
      maxWidth={false}
      data-testid="cancellation-reason-modal"
      PaperProps={{
        className: styles.modalPaper,
      }}
    >
      <IconButton
        onClick={isCanceled ? handleCloseConfirmation : onClose}
        className={styles.closeButton}
        data-testid="cancellation-modal-close"
        size="small"
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      {isCanceled ? (
        <>
          <DialogTitle className={styles.modalTitleContainer}>
            <Typography className={styles.modalTitle}>
              {t("CustomerOrderDetails.preOrder.cancelModal.canceledTitle", {
                defaultValue: "Preorder canceled",
              })}
            </Typography>
          </DialogTitle>

          <DialogContent className={styles.modalContent}>
            <Typography className={styles.descriptionText}>
              {t("CustomerOrderDetails.preOrder.cancelModal.canceledBody", {
                defaultValue:
                  "This preorder is now canceled. The customer will be notified that their order was canceled.",
              })}
            </Typography>
          </DialogContent>

          <DialogActions className={styles.modalActions}>
            <EMPButton
              variant="contained"
              onClick={handleCloseConfirmation}
              isFord
              testId="cancellation-modal-close-success"
              sx={buttonSx}
            >
              {t("CustomerOrderDetails.preOrder.cancelModal.closeButton", {
                defaultValue: "Close",
              })}
            </EMPButton>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle className={styles.modalTitleContainer}>
            <Typography className={styles.modalTitle}>
              {t("CustomerOrderDetails.preOrder.cancelModal.title", {
                defaultValue: "Cancel preorder",
              })}
            </Typography>
          </DialogTitle>

          <DialogContent className={styles.modalContent}>
            <Typography className={styles.descriptionText}>
              {t("CustomerOrderDetails.preOrder.cancelModal.body", {
                defaultValue:
                  "Once you cancel the preorder request, you can't restore it. Please share the reason to cancel.",
              })}
            </Typography>

            <Box className={styles.cancellationReasonSection}>
              <Typography className={styles.reasonLabel}>
                {t("CustomerOrderDetails.preOrder.cancelModal.reasonLabel", {
                  defaultValue: "Reason for cancellation",
                })}
              </Typography>

              <FormControl className={styles.reasonFormControl}>
                <EMPSelectField
                  testId="cancellation-reason-select"
                  value={selectedReason}
                  onChange={handleReasonChange}
                  menuItems={cancellationReasonMenuItems}
                  className={styles.reasonSelect}
                  inputProps={{
                    className: selectedReason
                      ? styles.reasonSelectValue
                      : styles.reasonSelectPlaceholder,
                    "aria-label": "Reason for cancellation",
                  }}
                />
              </FormControl>
            </Box>

            {isOtherReason && (
              <TextField
                fullWidth
                multiline
                minRows={2}
                className={styles.otherReasonInput}
                label={t(
                  "CustomerOrderDetails.preOrder.cancelModal.otherReasonLabel",
                  {
                    defaultValue: "Please specify",
                  }
                )}
                value={otherReason}
                data-testid="cancellation-other-reason"
                onChange={handleOtherReasonChange}
              />
            )}

            {errorMessage && (
              <Typography
                role="alert"
                color="error"
                data-testid="cancellation-modal-error"
              >
                {errorMessage}
              </Typography>
            )}
          </DialogContent>

          <DialogActions className={styles.modalActions}>
            <EMPButton
              variant="outlined"
              onClick={onGoBack}
              disabled={isSubmitting}
              isFord
              testId="cancellation-modal-go-back"
              sx={buttonSx}
            >
              {t("CustomerOrderDetails.preOrder.cancelModal.goBackButton", {
                defaultValue: "Go Back",
              })}
            </EMPButton>

            <EMPButton
              variant="contained"
              onClick={handleCancelOrder}
              disabled={isCancelDisabled}
              isFord
              testId="cancellation-modal-cancel-order"
              sx={buttonSx}
            >
              {t(
                "CustomerOrderDetails.preOrder.cancelModal.cancelOrderButton",
                {
                  defaultValue: "Cancel Order",
                }
              )}
            </EMPButton>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

CancellationReasonModal.propTypes = {
  open: PropTypes.bool.isRequired,
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onClose: PropTypes.func.isRequired,
  onGoBack: PropTypes.func.isRequired,
  onCancelOrder: PropTypes.func.isRequired,
  onCancelSuccess: PropTypes.func.isRequired,
};

export default CancellationReasonModal;
